import { get, post, patch, del } from '../httpMethods';
import httpService from '../httpService';
import { ApiResponse } from '../../types/api';

export interface Designation {
  id: string;
  name: string;
  department?: string;
  level?: string;
  createdAt: string;
}

export interface Shift {
  id: string;
  shiftCode: string;
  name: string;
  startTime: string;
  endTime: string;
  color?: string;
  description?: string;
  breakMinutes?: number;
  displayOrder?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface HrEmployee {
  id: string;
  employeeCode?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  photoUrl?: string;
  department?: { name: string };
  designation?: { name: string };
  bankName?: string;
  bankAccountNumber?: string;
}

export interface Attendance {
  id: string;
  employeeId: string;
  employee?: HrEmployee | null;
  date: string;
  status: 'PRESENT' | 'ABSENT' | 'LATE' | 'HALF_DAY' | 'ON_LEAVE' | 'HOLIDAY';
  checkIn?: string;
  checkOut?: string;
  shiftType?: string;
  shiftId?: string;
  shift?: Shift | null;
  notes?: string;
  createdAt: string;
}

export interface Leave {
  id: string;
  employeeId: string;
  employee?: HrEmployee | null;
  type: string;
  startDate: string;
  endDate: string;
  days?: number;
  reason?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  approvedById?: string;
  approvedAt?: string;
  createdAt: string;
}

export interface PayrollAllowanceItem {
  label: string;
  amount: number;
}

export interface PayrollDeductionItem {
  reason: string;
  amount: number;
}

export interface Payroll {
  id: string;
  employeeId: string;
  employee?: HrEmployee | null;
  payPeriod: string;
  periodStart?: string;
  periodEnd?: string;
  basicSalary: number;
  allowances: number;
  allowanceItems?: PayrollAllowanceItem[];
  deductions: number;
  deductionItems?: PayrollDeductionItem[];
  taxDeduction: number;
  grossSalary: number;
  netSalary: number;
  // Payroll-engine breakdown (present on records generated by the engine).
  workingDays?: number;
  payableDays?: number;
  lopDays?: number;
  overtimeHours?: number;
  overtimeAmount?: number;
  leaveEncashment?: number;
  advanceDeduction?: number;
  loanDeduction?: number;
  status: 'DRAFT' | 'PROCESSED' | 'FINALIZED' | 'PAID';
  paidAt?: string;
  paymentReference?: string;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
}

export type SalaryAdvanceStatus = 'PENDING' | 'APPROVED' | 'ACTIVE' | 'CLOSED' | 'CANCELLED';
export type SalaryAdvanceRecoveryMethod = 'ONE_TIME' | 'INSTALLMENT';

export interface SalaryAdvance {
  id: string;
  employeeId: string;
  employee?: HrEmployee | null;
  amount: number;
  issuedDate: string;
  reason?: string | null;
  status: SalaryAdvanceStatus;
  recoveryMethod: SalaryAdvanceRecoveryMethod;
  installmentAmount?: number | null;
  installments?: number | null;
  outstandingBalance: number;
  approvedById?: string | null;
  approvedAt?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface SalaryAdvanceRecovery {
  id: string;
  advanceId: string;
  payrollId?: string | null;
  employeeId: string;
  amount: number;
  recoveredAt: string;
  notes?: string | null;
  createdAt: string;
}

export type DailyRateBasis = 'CALENDAR_DAYS' | 'FIXED_DAYS' | 'WORKING_DAYS';

export interface PayrollPolicy {
  id: string;
  name: string;
  departmentId?: string | null;
  department?: { id: string; name: string } | null;
  isDefault: boolean;
  dailyRateBasis: DailyRateBasis;
  fixedWorkingDays: number;
  standardHoursPerDay: number;
  overtimeMultiplier: number;
  holidayOvertimeMultiplier: number;
  maxOvertimeHours: number;
  overtimeGraceMinutes: number;
  overtimeRoundingMinutes: number;
  minimumOvertimeMinutes: number;
  maxOvertimeHoursPerDay: number;
  maxOvertimeHoursPerWeek: number;
  prorateAllowances: boolean;
  weeklyOffDays: number[];
  latePenaltyDays: number;
  absentDeductionMultiplier: number;
  deductUnapprovedAbsence: boolean;
  minimumNetPay: number;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

/** The resolved policy that actually applies to a department. */
export interface EffectivePayrollPolicy {
  dailyRateBasis: DailyRateBasis;
  fixedWorkingDays: number;
  standardHoursPerDay: number;
  overtimeMultiplier: number;
  holidayOvertimeMultiplier: number;
  maxOvertimeHours: number;
  prorateAllowances: boolean;
  weeklyOffDays: number[];
  latePenaltyDays: number;
  absentDeductionMultiplier: number;
  deductUnapprovedAbsence: boolean;
  minimumNetPay: number;
}

export interface SalaryAdvancePayload {
  employeeId: string;
  amount: number;
  issuedDate: string;
  reason?: string;
  recoveryMethod?: SalaryAdvanceRecoveryMethod;
  installmentAmount?: number;
  installments?: number;
  notes?: string;
}

export interface SalaryAdvanceQueryParams {
  employeeId?: string;
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export type PayrollPolicyPayload = Omit<
  PayrollPolicy,
  'id' | 'department' | 'createdAt' | 'updatedAt'
>;

export interface BulkGeneratePayrollResult {
  created: number;
  updated: number;
  skipped: number;
  total: number;
  periodStart: string;
  periodEnd: string;
}

export interface BulkGeneratePayrollPayload {
  payPeriod: string;
  employeeIds?: string[];
  departmentId?: string;
  overwrite?: boolean;
  notes?: string;
}

export interface AttendanceQueryParams {
  employeeId?: string;
  dateFrom?: string;
  dateTo?: string;
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface LeaveQueryParams {
  employeeId?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export const hrService = {
  getDesignations: (): Promise<ApiResponse<Designation[]>> => {
    return get<Designation[]>('/hr/designations');
  },

  createDesignation: (data: any): Promise<ApiResponse<Designation>> => {
    return post<Designation>('/hr/designations', data);
  },

  // Shifts (shift master)
  getShifts: (activeOnly = false): Promise<ApiResponse<Shift[]>> => {
    return get<Shift[]>(`/hr/shifts${activeOnly ? '?active=true' : ''}`);
  },

  createShift: (data: Partial<Shift>): Promise<ApiResponse<Shift>> => {
    return post<Shift>('/hr/shifts', data);
  },

  updateShift: (id: string, data: Partial<Shift>): Promise<ApiResponse<Shift>> => {
    return patch<Shift>(`/hr/shifts/${id}`, data);
  },

  deleteShift: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/hr/shifts/${id}`);
  },

  getAttendance: (employeeId?: string, dateFrom?: string, dateTo?: string): Promise<ApiResponse<Attendance[]>> => {
    const params = new URLSearchParams();
    if (employeeId) params.append('employeeId', employeeId);
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    const query = params.toString() ? `?${params.toString()}` : '';
    return get<Attendance[]>(`/hr/attendance${query}`);
  },

  createAttendance: (data: any): Promise<ApiResponse<Attendance>> => {
    return post<Attendance>('/hr/attendance', data);
  },

  getAttendancePaginated: (params: AttendanceQueryParams = {}): Promise<ApiResponse<Attendance[]>> => {
    const query = new URLSearchParams();
    if (params.employeeId) query.append('employeeId', params.employeeId);
    if (params.dateFrom) query.append('dateFrom', params.dateFrom);
    if (params.dateTo) query.append('dateTo', params.dateTo);
    if (params.status) query.append('status', params.status);
    if (params.search) query.append('search', params.search);
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    const qs = query.toString();
    return get<Attendance[]>(`/hr/attendance${qs ? `?${qs}` : ''}`);
  },

  updateAttendance: (id: string, data: any): Promise<ApiResponse<Attendance>> => {
    return patch<Attendance>(`/hr/attendance/${id}`, data);
  },

  deleteAttendance: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/hr/attendance/${id}`);
  },

  bulkDeleteAttendance: (ids: string[]): Promise<ApiResponse<{ deleted: number }>> => {
    return post<{ deleted: number }>('/hr/attendance/bulk-delete', { ids });
  },

  bulkAssignShift: (data: {
    employeeIds: string[];
    shiftId: string;
    dateFrom: string;
    dateTo: string;
    weekdays?: number[];
    skipWeekends?: boolean;
    skipWeeklyOffDays?: boolean;
    notes?: string;
  }): Promise<ApiResponse<{ assigned: number; created: number; updated: number }>> => {
    return post('/hr/attendance/bulk', data);
  },

  getLeave: (employeeId?: string, status?: string, dateFrom?: string, dateTo?: string): Promise<ApiResponse<Leave[]>> => {
    const params = new URLSearchParams();
    if (employeeId) params.append('employeeId', employeeId);
    if (status) params.append('status', status);
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    const query = params.toString() ? `?${params.toString()}` : '';
    return get<Leave[]>(`/hr/leave${query}`);
  },

  createLeave: (data: any): Promise<ApiResponse<Leave>> => {
    return post<Leave>('/hr/leave', data);
  },

  getLeavePaginated: (params: LeaveQueryParams = {}): Promise<ApiResponse<Leave[]>> => {
    const query = new URLSearchParams();
    if (params.employeeId) query.append('employeeId', params.employeeId);
    if (params.status) query.append('status', params.status);
    if (params.dateFrom) query.append('dateFrom', params.dateFrom);
    if (params.dateTo) query.append('dateTo', params.dateTo);
    if (params.search) query.append('search', params.search);
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    const qs = query.toString();
    return get<Leave[]>(`/hr/leave${qs ? `?${qs}` : ''}`);
  },

  approveLeave: (id: string, data: any): Promise<ApiResponse<Leave>> => {
    return post<Leave>(`/hr/leave/${id}/approve`, data);
  },

  updateLeave: (id: string, data: any): Promise<ApiResponse<Leave>> => {
    return patch<Leave>(`/hr/leave/${id}`, data);
  },

  deleteLeave: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/hr/leave/${id}`);
  },

  bulkDeleteLeave: (ids: string[]): Promise<ApiResponse<{ deleted: number }>> => {
    return post<{ deleted: number }>('/hr/leave/bulk-delete', { ids });
  },

  getPayroll: (params?: {
    employeeId?: string;
    payPeriod?: string;
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<Payroll[]>> => {
    const query = new URLSearchParams();
    if (params?.employeeId) query.append('employeeId', params.employeeId);
    if (params?.payPeriod) query.append('payPeriod', params.payPeriod);
    if (params?.status) query.append('status', params.status);
    if (params?.search) query.append('search', params.search);
    if (params?.page) query.append('page', String(params.page));
    if (params?.limit) query.append('limit', String(params.limit));
    const qs = query.toString();
    return get<Payroll[]>(`/hr/payroll${qs ? `?${qs}` : ''}`);
  },

  getPayrollById: (id: string): Promise<ApiResponse<Payroll>> => {
    return get<Payroll>(`/hr/payroll/${id}`);
  },

  createPayroll: (data: any): Promise<ApiResponse<Payroll>> => {
    return post<Payroll>('/hr/payroll', data);
  },

  updatePayroll: (
    id: string,
    data: {
      status?: string;
      paymentReference?: string;
      deductionItems?: PayrollDeductionItem[];
      notes?: string;
    },
  ): Promise<ApiResponse<Payroll>> => {
    return patch<Payroll>(`/hr/payroll/${id}`, data);
  },

  bulkGeneratePayroll: (data: BulkGeneratePayrollPayload): Promise<ApiResponse<BulkGeneratePayrollResult>> => {
    return post<BulkGeneratePayrollResult>('/hr/payroll/bulk-generate', data);
  },

  bulkUpdatePayrollStatus: (data: {
    ids: string[];
    status: string;
    paymentReference?: string;
  }): Promise<ApiResponse<{ updated: number }>> => {
    return post<{ updated: number }>('/hr/payroll/bulk-status', data);
  },

  deletePayroll: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/hr/payroll/${id}`);
  },

  bulkDeletePayroll: (ids: string[]): Promise<ApiResponse<{ deleted: number }>> => {
    return post<{ deleted: number }>('/hr/payroll/bulk-delete', { ids });
  },

  /** Fetch the generated payslip PDF for a payroll record as a Blob. */
  downloadPayslip: async (id: string): Promise<Blob> => {
    const response = await httpService.get(`/hr/payroll/${id}/payslip`, {
      responseType: 'blob',
    });
    return new Blob([response.data], { type: 'application/pdf' });
  },

  /** Fetch the monthly bank salary statement as a PDF Blob. */
  downloadBankSalarySheetPdf: async (payPeriod: string, status?: string): Promise<Blob> => {
    const query = new URLSearchParams({ payPeriod });
    if (status) query.append('status', status);
    const response = await httpService.get(`/hr/payroll/bank-salary-sheet/pdf?${query.toString()}`, {
      responseType: 'blob',
    });
    return new Blob([response.data], { type: 'application/pdf' });
  },

  // ── Salary advances ──────────────────────────────────────────────────────
  getSalaryAdvances: (params: SalaryAdvanceQueryParams = {}): Promise<ApiResponse<SalaryAdvance[]>> => {
    const query = new URLSearchParams();
    if (params.employeeId) query.append('employeeId', params.employeeId);
    if (params.status) query.append('status', params.status);
    if (params.search) query.append('search', params.search);
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    const qs = query.toString();
    return get<SalaryAdvance[]>(`/hr/salary-advances${qs ? `?${qs}` : ''}`);
  },

  getSalaryAdvanceById: (id: string): Promise<ApiResponse<SalaryAdvance>> => {
    return get<SalaryAdvance>(`/hr/salary-advances/${id}`);
  },

  getSalaryAdvanceRecoveries: (id: string): Promise<ApiResponse<SalaryAdvanceRecovery[]>> => {
    return get<SalaryAdvanceRecovery[]>(`/hr/salary-advances/${id}/recoveries`);
  },

  createSalaryAdvance: (data: SalaryAdvancePayload): Promise<ApiResponse<SalaryAdvance>> => {
    return post<SalaryAdvance>('/hr/salary-advances', data);
  },

  approveSalaryAdvance: (id: string): Promise<ApiResponse<SalaryAdvance>> => {
    return post<SalaryAdvance>(`/hr/salary-advances/${id}/approve`, {});
  },

  updateSalaryAdvance: (
    id: string,
    data: Partial<SalaryAdvancePayload> & { status?: SalaryAdvanceStatus; outstandingBalance?: number },
  ): Promise<ApiResponse<SalaryAdvance>> => {
    return patch<SalaryAdvance>(`/hr/salary-advances/${id}`, data);
  },

  deleteSalaryAdvance: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/hr/salary-advances/${id}`);
  },

  // ── Payroll policy ───────────────────────────────────────────────────────
  getPayrollPolicies: (): Promise<ApiResponse<PayrollPolicy[]>> => {
    return get<PayrollPolicy[]>('/hr/payroll-policy');
  },

  getEffectivePayrollPolicy: (departmentId?: string): Promise<ApiResponse<EffectivePayrollPolicy>> => {
    const qs = departmentId ? `?departmentId=${encodeURIComponent(departmentId)}` : '';
    return get<EffectivePayrollPolicy>(`/hr/payroll-policy/effective${qs}`);
  },

  createPayrollPolicy: (data: PayrollPolicyPayload): Promise<ApiResponse<PayrollPolicy>> => {
    return post<PayrollPolicy>('/hr/payroll-policy', data);
  },

  updatePayrollPolicy: (id: string, data: Partial<PayrollPolicyPayload>): Promise<ApiResponse<PayrollPolicy>> => {
    return patch<PayrollPolicy>(`/hr/payroll-policy/${id}`, data);
  },

  deletePayrollPolicy: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/hr/payroll-policy/${id}`);
  },

  bulkDeletePayrollPolicies: (ids: string[]): Promise<ApiResponse<{ deleted: number }>> => {
    return post<{ deleted: number }>('/hr/payroll-policy/bulk-delete', { ids });
  },
};
